/**
 * Cross-Environment Firebase
 *
 * Initializes a secondary read-only Firebase app pointing at the OTHER
 * deployment (test ↔ prod). This allows the Admin User Activity tab to
 * display session presence across both environments simultaneously.
 *
 * To enable, add REACT_APP_CROSS_ENV_FIREBASE_* variables to your .env files:
 *   .env.test       → put the PRODUCTION Firebase credentials here
 *   .env.production → put the TEST Firebase credentials here
 *
 * If the variables are absent, crossDb is null and the feature is silently
 * disabled (single-environment display only).
 */

import { initializeApp, getApps } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';

// ─── Environment labels ───────────────────────────────────────────────────────

/** Display label for the environment this build is running in. */
export const CURRENT_ENV_LABEL =
  process.env.REACT_APP_FIREBASE_PROJECT_ID === 'treegenie-test' ? 'Test' : 'Prod';

/** Display label for the OTHER (cross) environment. */
export const CROSS_ENV_LABEL = CURRENT_ENV_LABEL === 'Test' ? 'Prod' : 'Test';

/** Lowercase env identifier stored in Firestore userPresence docs. */
export const CURRENT_ENV = CURRENT_ENV_LABEL.toLowerCase();

// ─── Secondary app initialisation ────────────────────────────────────────────

const crossConfig = {
  apiKey:            process.env.REACT_APP_CROSS_ENV_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_CROSS_ENV_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_CROSS_ENV_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_CROSS_ENV_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_CROSS_ENV_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_CROSS_ENV_FIREBASE_APP_ID,
};

let crossDb = null;

if (crossConfig.apiKey && crossConfig.projectId) {
  try {
    const app =
      getApps().find(a => a.name === 'cross-env') ||
      initializeApp(crossConfig, 'cross-env');
    crossDb = getFirestore(app);
  } catch (e) {
    console.warn('[CrossEnv] Firebase init failed — cross-env presence disabled:', e.message);
  }
}

export { crossDb };
